import DayView from './DayView.js';
import './DayResourceCalendarRow.js';
import DomHelper from '../../Core/helper/DomHelper.js';
/**
 * @module Calendar/widget/DayResourceView
 */
/**
 * A calendar view showing resources grouped by date, it is normally used as a {@link Calendar.view.Calendar#config-modes mode}
 * of a Calendar but may be used standalone as a regular Widget.
 *
 * By default, it shows a one week range encapsulating the Calendar's current {@link Calendar.view.Calendar#property-date}.
 * This can be configured using the {@link #config-range} config property.
 *
 * {@inlineexample Calendar/widget/DayResourceView.js}
 *
 * @demo Calendar/date-resource
 *
 * @extends Calendar/widget/DayView
 * @classtype dayresource
 * @typingswidget
 */
export default class DayResourceView extends DayView {
    static $name = 'DayResourceView';
    static type = 'dayresource';
    static configurable = {
        /**
         * The minimum width of a resource column.
         *
         * If this is set, and the day columns columns overflow the horizontal space available, the columns
         * will be scrollable horizontally in the normal way.
         *
         * There is a {@link #property-horizontalScroller} property which handles scrolling in this
         * dimension.
         * @prp {Number|String}
         */
        minResourceWidth : null,
        /**
         * By default, resource avatars are shown in the resource header if there is horizontal space.
         * Configure this as `false` to inhibit this.
         * @prp {Boolean}
         */
        showAvatars : true,
        title : 'L{dayResourceView}',
        allDayEvents : {
            type : 'dayresourcecalendarrow'
        },
        range : '1w',
        /**
         * Configure as `true` to show avatars of the resources (calendars) alongside
         * resource names in resource column headers.
         *
         * Configure as `'last'` to show avatars of the assigned resources (calendars) after the name.
         *
         * Note that you must set {@link #config-resourceImagePath} in order that the system
         * knows where to access the resource's image file from.
         *
         * If no image is set, or the image is not found, the resource's initials are shown instead.
         *
         * By default it is inherited from the owning Calendar:
         * ```javascript
         * new Calendar({
         *     resourceImagePath   : 'images/resources/'
         *     modes : {
         *         dayresource : {
         *             showHeaderAvatars : true,
         *         }
         *     }
         * });
         * ```
         * @config {Boolean|'last'}
         */
        showHeaderAvatars : true
    };
    doRefresh() {
        this.updateMinResourceWidth(this.minResourceWidth);
        super.doRefresh(...arguments);
    }
    checkAutoCreateGesture(domEvent, date, resourceRecord) {
        if (resourceRecord) {
            return super.checkAutoCreateGesture(...arguments);
        }
    }
    // Called as an onFunction by updateElementLayout triggering 'layoutUpdate'.
    onLayoutUpdate() {
        this.syncNarrowResourceWidth();
        super.onLayoutUpdate?.(...arguments);
    }
    onDayContainerResize() {
        this.syncNarrowResourceWidth();
        return super.onDayContainerResize?.(...arguments);
    }
    syncNarrowResourceWidth() {
        const resourceHeader = this.element.querySelector('.b-dayresourcecalendarrow-resource-header');
        if (resourceHeader) {
            this.allDayEvents.element.classList.toggle('b-narrow-resource-header', resourceHeader.offsetWidth < 100);
        }
    }
    updateMinResourceWidth(minResourceWidth) {
        const
            me          = this,
            { element } = me,
            { records } = me.resourceStore;
        if (minResourceWidth) {
            const
                resourceFilter = me.calendar?.widgetMap.resourceFilter,
                resources      = resourceFilter ? records.filter(c => resourceFilter.value.includes(c)) : records;
            element.classList.add('b-has-min-resource-width');
            element.style.setProperty('--min-resource-width', DomHelper.setLength(minResourceWidth));
            element.style.setProperty('--min-day-width', `calc(${DomHelper.setLength(minResourceWidth)} * ${resources.length})`);
        }
        else {
            element.classList.remove('b-has-min-resource-width');
            element.style.setProperty('--min-resource-width', null);
            me.updateMinDayWidth(me.minDayWidth);
        }
        me.updateElementLayout();
        me.allDayEvents?.updateMinResourceWidth(minResourceWidth);
    }
    updateShowHeaderAvatars(showHeaderAvatars) {
        const { allDayEvents : a } = this;
        if (a) {
            a.resourceImagePath   = this.resourceImagePath;
            a.showHeaderAvatars = showHeaderAvatars;
        }
    }
    updateWeekStartDay(weekStartDay, oldWeekStartDay) {
        super.updateWeekStartDay?.(weekStartDay);
        if (!this.isConfiguring) {
            const startDate = new Date(this.startDate);
            startDate.setDate(startDate.getDate() + weekStartDay - oldWeekStartDay);
            this.startDate = startDate;
        }
    }
    // Override from DayView.
    // This returns a DayView day column config for each resource
    getDayDomConfig(date) {
        const
            me       = this,
            children = [];
        // hidden non-working day results in an empty config
        if (!me.hiddenNonWorkingDays[date.getDay()]) {
            const
                {
                    calendar,
                    resourceStore
                }              = me,
                resourceFilter = calendar?.widgetMap.resourceFilter,
                resources      = resourceFilter ? resourceStore.records.filter(c => resourceFilter.value.includes(c)) : resourceStore.records,
                key            = me.dayTime.dateKey(date),
                cellData       = me.cellMap.get(key) || me.createCellData(date),
                events         = cellData?.events;
            // One sub-column for each resource
            for (let i = 0, { length } = resources; i < length; i++) {
                const resource = me.resource = resources[i];
                // Various renderers detect whether the view owns a Resource.
                // While processing a resource column for this day, we must own that resource.
                me.resourceId = resource.id;
                // Filter down day's events to only those for this resource column
                cellData && (cellData.events = events.filter(e => e.resources?.includes(resource)));
                const dayColumn = super.getDayDomConfig(date);
                delete dayColumn.dataset.date;
                dayColumn.dataset.resourceId = resource.id;
                children.push(dayColumn);
                // We only own a resource when creating a resource column for a day
                me.resource = me.resourceId = null;
            }
            // Restore day's full event load
            cellData && (cellData.events = events);
            // A single day's column encapsulates all resources
            return {
                className : {
                    'b-dayresourceview-column'        : 1,
                    [`b-day-of-week-${cellData.day}`] : 1
                },
                dataset : {
                    date : key
                },
                children
            };
        }
    }
}
DayResourceView.initClass();
DayResourceView._$name = 'DayResourceView';